import { ActivityType, ITransitionJourneyTreeNode, ITransitionJourneyWorkLog, TransitionJourneyNodeType } from "../interfaces/AppInterfaces";
import { getEnv } from "../utils/envConfig";

export const INPUT_TYPES = {
    TICKET_DETAILS: 'TicketDetails',
    FILE_UPLOAD: 'FileUpload',
    TICKET_LIST: 'TicketList',
    SIMPLE_TEXT: 'SimpleText',
    AUDIO: 'Audio'
};

export const FIELD_TYPES = {
    TEXT: 'text',
    DROPDOWN: 'dropdown',
    DATE_RANGE: 'date_range'
};

export const DARK_THEME = 'dark';
export const LIGHT_THEME = 'light';
export const NOTIFICATION_BACKGROUND = {
    DARK: "#2A2D31",
    LIGHT: "#EEEEEE",
    DARK_ACKNOWLEDGE: "#1d1d1f",
    LIGHT_ACKNOWLEDGE: "#f5f5f5"

}
export const AGENTIC_MS_USER_SELECTED_THEME = 'AGENTIC_MS_USER_SELECTED_THEME';

export const CHAT_INPUT_SIDE = {
    USER: 'UserSide',
    AGENT: 'AgentSide'
};

export const TRANSITION_JOURNEY_ACTIVITY_TYPE = {
    AI_AGENT: 'aiAgent',
    USER: 'user',
    CONNECTORS: 'connectors',
    TEAM_INVITATION: 'teamInvitation',
    PROJECT_DETAILS: 'projectDetails',
    SMART_CAUSAL_CONFIG: 'smartCausalConfig',
}

export const SMART_CAUSAL_DOWNLOAD_FILE_TYPE = {
    CLUSTERING: 'clustering',
    PROCESSED: 'processed',
    ANAMOLIES: 'anamolies',
    NOT_PROCESSED: 'not_processed',
}

export const CHAT_INPUT_STATE = {
    SUCCESS: 'Success',
    WARNING: 'Warning',
    ERROR: 'Error',
    NEW: 'New'
}

export const THEME_BACKGROUND_DEFAULT_COLORS = {
    DARK: '#151515',
    LIGHT: '#FCFCFD'
}

export const TICKET_PRIORITY = {
    CRITICAL: 'Critical',
    HIGH: 'High',
    MEDIUM: 'Medium',
    MODERATE: 'Moderate',
    LOW: 'Low',
    PLANNING: 'Planning',
}

export const TICKET_STATUS = {
    NEW: 'New',
    IN_PROGRESS: 'In Progress',
    ON_HOLD: 'On Hold',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
    CANCELED: 'Canceled',
    ASSIGNED: 'Assigned'
}

export const SMTD_FILES_REPROCESSING_STATUS = {
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    ERROR: 'Error'
}

export const TICKET_DETAILS_VIEW_TYPE = {
    LANDSCAPE: 'Landscape',
    PORTRAIT: 'Portrait'
}

export const ALERT_SEVERITY = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    SUCCESS: 'success'
}


export const VALIDATION_ERROR_MESSAGES = {
    NO_USER_QUERY_ENTERED: 'Please enter your query or select file or record audio.'
}

export const TABLE_ROWS_PER_PAGE = [1, 5, 10, 25];
export const TABLE_DEFAULT_PAGE_SIZE = 9;
export const WORKLOG_PAGE_SIZE = 10;
export const CONNECTOR_PAGE_SIZE = 10;
export const AGENTIC_MS_USER_INFO = 'AGENTIC_MS_USER_INFO';
export const AUTHORIZATION_TOKEN = 'AUTHORIZATION_TOKEN';
export const SESSION_STORAGE_KEY = 'SESSION';
export const AGENTIC_MS_USER_SELECTED_ROLE = 'AGENTIC_MS_USER_SELECTED_ROLE';
export const SELECTED_ASSESSMENT_PARAMS = 'SELECTED_ASSESSMENT_PARAMS';

export let CURRENT_USER_SELECTED_ROLE: string = '';

export const setCurrentUserSelectedRole = (role: string) => {
    CURRENT_USER_SELECTED_ROLE = role;
    sessionStorage.setItem(AGENTIC_MS_USER_SELECTED_ROLE, role);
};

export const getCurrentUserSelectedRole = () => {
    if (!CURRENT_USER_SELECTED_ROLE) {
        CURRENT_USER_SELECTED_ROLE = sessionStorage.getItem(AGENTIC_MS_USER_SELECTED_ROLE) || '';
    }
    return CURRENT_USER_SELECTED_ROLE;
};

export const initializeUserSelectedRole = () => {
    const storedRole = sessionStorage.getItem(AGENTIC_MS_USER_SELECTED_ROLE);
    if (storedRole) {
        CURRENT_USER_SELECTED_ROLE = storedRole;
    }
};

export const PERSONAS = {
    TRANSITION: 'transition',
    END_USER: 'end_user'
}

export const PERSONAS_NAME = {
    TRANSITION: 'Transition',
    END_USER: 'End User'
}

/**
 * Extracts the bare role from a potentially prefixed role string.
 * e.g. "transition/transition_manager" → "transition_manager"
 *      "support_analyst" → "support_analyst"
 */
export const extractBareRole = (role: string): string => {
    return role.includes('/') ? role.split('/')[1] : role;
};

/**
 * Extracts the persona (parent) prefix from a prefixed role string.
 * e.g. "transition/transition_manager" → "transition"
 *      "support_analyst" → null
 */
export const extractPersona = (role: string): string | null => {
    return role.includes('/') ? role.split('/')[0] : null;
};

/**
 * Checks if a user's roles array contains a specific bare role.
 * Works whether roles are prefixed ("transition/transition_manager") or not ("transition_manager").
 */
export const userHasRole = (userRoles: string[] | undefined | null, targetRole: string): boolean => {
    if (!userRoles) return false;
    return userRoles.some(r => extractBareRole(r) === targetRole);
};

/**
 * Finds the first matching prefixed role entry for a given bare role,
 * and returns the persona (parent) prefix, or null if the role is not prefixed.
 */
export const getPersonaForRole = (userRoles: string[] | undefined | null, targetRole: string): string | null => {
    if (!userRoles) return null;
    const match = userRoles.find(r => extractBareRole(r) === targetRole);
    return match ? extractPersona(match) : null;
};

export const ROLES = {
    SUPPORT_ENGINEER: 'support_engineer',
    END_USER: 'business_user',
    TRANSITION_MANAGER: 'transition_manager',
    OPERATION_MANAGER: 'operation_manager',
    TRANSITION_DIRECTOR: 'transition_director',
    TRANSITION_ADMIN: 'transition_admin',
    AGENT_METRICS: 'Agent Metrics',
    IT_MANAGER: 'it_manager',
    SUPPORT_ANALYST: 'support_analyst' // Changed to match backend value (lowercase with underscore)
};
export const ROLES_NAME = {
    SUPPORT_ENGINEER: 'Support Engineer',
    END_USER: 'Business User',
    TRANSITION_MANAGER: 'Transition Manager',
    OPERATION_MANAGER: 'Operation Manager',
    TRANSITION_DIRECTOR: 'Transition Director',
    TRANSITION_ADMIN: 'Transition Admin',
    AGENT_METRICS: 'Agent Metrics',
    IT_MANAGER: 'IT Manager',
    SUPPORT_ANALYST: 'Support Analyst'
};

export const PROJECT_IDS = {
    SUPPORT_ANALYST: 'support_analyst'
};

export const DATE_RANGES = {
    TODAY: 'today',
    THIS_WEEK: 'thisweek',
    LAST_WEEK: 'lastweek',
    LAST_MONTH: 'lastmonth',
    CUSTOM: 'custom',
};

export const PREDEFINED_DATE_RANGES = {
    TODAY: 'today',
    YESTERDAY: 'yesterday',
    LAST_7_DAYS: 'last_7_days',
    LAST_30_DAYS: 'last_30_days',
    THIS_WEEK: 'this_week',
    LAST_WEEK: 'last_week',
    THIS_MONTH: 'this_month',
    LAST_MONTH: 'last_month',
    CUSTOM: 'custom'
};

export const CONNECTOR_TYPES = {
    SHARE_POINT: 'sharepoint',
    SERVICE_NOW: 'servicenow',
    GOOGLE_WORKSPACE: 'google_workspace'
};

export const WORK_LOG_TYPES = {
    GLOBAL: 'Global',
    PERSONA_LEVEL: 'PersonaLevel',
    PROJECT_LEVEL: 'ProjectLevel',
    CONNECTOR_LEVEL: 'ConnectorLevel',
    JOURNEY_SECTION_LEVEL: 'JourneySectionLevel',
    TEAM_MEMBERS: "TeamMembers"
};

export type WorkLogType = typeof WORK_LOG_TYPES[keyof typeof WORK_LOG_TYPES];

export const WORK_LOG_ITEM_TYPES = {
    KNOWLEDGE_ARTIFACTS: 'Knowledge Artifacts',
    KNOWLEDGE_ASSESSMENTS: "Knowledge Assessments",
    TICKETS: 'Tickets',
    SMTDS: 'SMTDs',
    CONNECTORS: 'Connectors',
    TEAM_MEMBERS: 'Team Members',
    KT_TOPICS: 'KT Topics',
    KT_STATUS: "KT Status",
    KT_REVIEW: "KT Review",
    SUMMARY_DOCUMENTS: 'Summary Documents',
    SOPS: 'SOPs',
    KT_SESSIONS: 'KT Sessions',
    PLAYBACK_SESSIONS: 'Playback Sessions',
    PLAYBACK_ASSESSMENT: "Playback Assessments",
    ASSESSMENT_QUESTIONS: 'Assessment Questions',
    QUESTIONS: 'Questions',
    ASSESSMENTS: 'Assessments',
    GAPS: 'Gaps',
    PROJECT: "Projects",
    USERS: "Users",
    SYSTEM: "System",
    APP_QUESTIONS: "App Questions",
    TRANSITION_REVIEW: "Transition Review",
    PLATFORM_HOTSPOTS: "Hotspots",
    WORK_LOG_ITEM: "WorkLogs",
} as const;

export type WorkLogItemType = typeof WORK_LOG_ITEM_TYPES[keyof typeof WORK_LOG_ITEM_TYPES];


export const ALL_MEDIA_TYPES = {
    PDF: `application/pdf`,
    RTF: 'text/rtf',
    DOCUMENT: `application/vnd.openxmlformats-officedocument.wordprocessingml.document`,
    ZIP: `application/zip`,
    JPEG: 'image/jpeg',
    JPG: 'image/jpg',
    PNG: 'image/png',
    TXT: `text/plain`,
    MP3: 'audio/mpeg',
    MP4: 'video/mp4',
    WAV: 'audio/wav',
    AVI: 'video/avi',
}

export const DOCUMENT_TYPES: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'text/rtf': 'RTF',
    'application/zip': 'ZIP',
    'text/plain': 'TXT',
};

export const IMAGE_TYPES: Record<string, string> = {
    'image/jpeg': 'JPEG',
    'image/jpg': 'JPG',
    'image/png': 'PNG',
};

export const MEDIA_TYPES: Record<string, string> = {
    'audio/mpeg': 'MP3',
    'video/mp4': 'MP4',
    'audio/wav': 'WAV',
    'video/avi': 'AVI',
};

export const EXCLUDEDPATHS = ['/', '/login', '/azureAuth', '/getAToken']

export const priorityRoutes: any = {
    "end_user": "/businessUser",
    "support_engineer": "/L1Dashboard",
    "transition_manager": "/TransitionManagerDashboard",
    "transition_director": "/TransitionDirectorDashboard",
    "transition_admin": "/TransitionAdminDashboard",
    "operation_manager": "/OperationManagerDashboard",
    "it_manager": "/ITManagerDashboard"
};

export const PROMPTS = {
    SOP_FOR_ARBIA: 'Fetch me SOP for Ariba ?',
    TRACK_ETA: 'how to track ETA of Employees in ARIBA',
    LOGIN_ISSUE: 'Not able to login to DigiQ ?',
    PROJECT_CLOSURE: 'How to perform Project closure in DIGIQ?',
    RESOLVE_SHIP_TO_ERROR_ARIBA: 'How to resolve ship to errors in Ariba ?'
};


export const OFFLINE_MSG = "Looks like you're offline. Please check your internet connection and try again.";
export const TRANSITION_JOURNEY_NODE_TYPE = {
    TEAM_COLLABORATION: 'team_collaboration',
    TEAM_INVITATION: 'team_invitation',
    CONNECTORS: 'connectors',
    CONFIGURE_KNOWLEDGE: 'configure_knowledge',
    AUTO_CREATE_STANDARD_DIRECTORY: 'auto_create_standard_directory',
    DERIVE_KNOWLEDGE: 'derive_knowledge',
    AUTOMATED_TICKET_ANALYSIS: 'automated_ticket_analysis',
    AUTO_CREATE_ADDITIONAL_FOLDER: 'auto_create_additional_folder',
    GENERATE_DETAILED_QUESTIONNAIRE: 'generate_detailed_questionnaire',
    KNOWLEDGE_COVERAGE_ASSESSMENT: 'knowledge_coverage_assessment',
    KNOWLEDGE_ASSESSMENT_KT_SESSION: 'knowledge_assessment_kt_session',
    SOP_GENERATION: 'sop_generation',
    SERVICE_REQUEST_GENERATION: 'service_request_generation',
    IDENTIFY_GAPS: 'identify_gaps',
    KNOWLEDGE_TRANSITION_PLAN: 'knowledge_transition_plan',
    GENERATING_QUESTIONS: 'generating_questions',
    GENERATING_KT_PLAN: 'generating_kt_plan',
    REVIEW_KT_PLAN: 'review_kt_plan',
    PUBLISH_KT_PLAN: 'publish_kt_plan',
    SCHEDULING_KT_SESSION: 'scheduling_kt_session',
    PROCESS_KT_VIDEO: 'process_kt_video',
    PREPARE_DOCUMENT: 'prepare_document',
    PERFORM_QUALITY_CHECK: 'perform_quality_check',
    GENERATE_SOP_DOCUMENT: 'generate_sop_document',
    PROCESS_REVERSE_KT_VIDEO: 'process_reverse_kt_video',
    GENERATE_MCQ: 'generate_mcq',
    SHARE_CERTIFICATION_TO_CUSTOMER: 'share_certification_to_customer',
    REVERSE_KT_TO_CUSTOMER: 'reverse_kt_to_customer',
    GENERATE_SMTD: 'generate_smtd',
    REVIEW_SMTD: 'review_smtd',
    TRANSITION_STATUS_REPORT: 'transition_status_report',
    TRANSITION_REVIEW: 'transition_review',
    CLOSURE_AND_SIGN_OFF: 'closure_and_sign_off',
    KT_SESSION: 'kt_session',
    KT_REVIEW: 'kt_review',
    KT_STATUS: 'kt_status',
    PLAYBACK: 'playback',
    PLAYBACK_SESSIONS: 'playback_sessions',
    PLAYBACK_ASSESSMENTS: 'playback_assessments',
    PERFORM: 'perform',
    OBSERVE: 'observe',
    PERFORM_AND_OBSERVE: 'perform_and_observe',
    LEARN: 'learn',
    KNOWLEDGE_WORKFLOW: 'knowledge_workflow',
    TRANSITION_CLOSURE: 'transition_closure',
    PROJECT_DETAILS: 'project_details',
    SMART_CAUSAL_CONFIG: 'smart_causal_config',
    GOVERNENCE_DASHBOARD: 'governence_dashboard'
};

export const TRANSITION_JOURNEY_NODE_LABEL = {
    TEAM_COLLABORATION: 'Team Collaboration',
    CONFIGURE_KNOWLEDGE: 'Configure Knowledge',
    AUTO_CREATE_STANDARD_DIRECTORY: 'Auto Create Standard Directory',
    DERIVE_KNOWLEDGE: 'Derive Knowledge',
    AUTOMATED_TICKET_ANALYSIS: 'Automated Ticket Analysis',
    AUTO_CREATE_ADDITIONAL_FOLDER: 'Auto Create Additional Folder',
    GENERATE_DETAILED_QUESTIONNAIRE: 'Generate Detailed Questionnaire',
    KNOWLEDGE_COVERAGE_ASSESSMENT: 'Knowledge Coverage Assessment',
    SOP_GENERATION: 'SOP Generation',
    SERVICE_REQUEST_GENERATION: 'Service Request Generation',
    IDENTIFY_GAPS: 'Identify Gaps',
    KNOWLEDGE_TRANSITION_PLAN: 'Knowledge Transition Plan',
    GENERATING_QUESTIONS: 'Generating Questions',
    GENERATING_KT_PLAN: 'Generating KT Plan',
    REVIEW_KT_PLAN: 'Review KT Plan',
    PUBLISH_KT_PLAN: 'Publish KT Plan',
    SCHEDULING_KT_SESSION: 'Scheduling KT Session',
    PROCESS_KT_VIDEO: 'Process KT Video',
    PREPARE_DOCUMENT: 'Prepare Document',
    PERFORM_QUALITY_CHECK: 'Perform Quality Check',
    GENERATE_SOP_DOCUMENT: 'Generate SOP Document',
    PROCESS_REVERSE_KT_VIDEO: 'Process Reverse KT Video',
    GENERATE_MCQ: 'Generate MCQ',
    SHARE_CERTIFICATION_TO_CUSTOMER: 'Share Certification to Customer',
    REVERSE_KT_TO_CUSTOMER: 'Reverse KT to Customer',
    GENERATE_SMTD: 'Generate SMTD',
    REVIEW_SMTD: 'Review SMTD',
    TRANSITION_STATUS_REPORT: 'Transition Status Report',
    CLOSURE_AND_SIGN_OFF: 'Closure and Sign Off',
};

export const TRANSITION_JOURNEY_NODE_DATA_ITEM_TYPE = {
    DASHBOARD: 'Dashboard',
    COLLABORATION: 'Collaboration',
    WORK_LOG: 'Work Log'
};

export const TRANSITION_JOURNEY_STATUS = {
    COMPLETED: 'COMPLETED',
    IN_PROGRESS: 'IN_PROGRESS',
    CREATED: 'CREATED',
    REQUIRED: 'REQUIRED'
}

export const TRANSITION_JOURNEY_PROJECT_STATUS = {
    COMPLETED: 'COMPLETED',
    IN_PROGRESS: 'IN PROGRESS',
    NEW: 'NEW'
}

export const TRANSITION_JOURNEY_CLOSURE_STATUS = {
    IN_PROGRESS: 'In Progress',
    APPROVED: 'Approved'
}

export const DOWNLOAD_FILE_TYPE = {
    CSV: 'csv',
    XLSX: 'xlsx',
    TEXT: 'txt',
    PDF: 'pdf',
    DOCX: 'docx'
}

export const SORT_BY = {
    UPDATED_AT: 'updated_at'
}

export const SORT_ORDER = {
    DESC: 'desc',
    ASC: 'asc'
}

export const DATE_ORDINAL = {
    TH: 'th',
    ST: 'st',
    ND: 'nd',
    RD: 'rd'
}

export const ARTIFACT_TYPE = {
    SOP: 'SOPs',
    SMTD: 'SMTDs',
    GAPS: 'Gaps',
    QUESTIONS: 'Questions',
    KT_SESSIONS: 'KT Sessions',
    KT_TOPICS: 'KT Topics',
    PLAYBACK_SESSION: 'Playback Sessions',
}

export const ARTIFACT_VIEW_TYPE = {
    SOP: 'SOP',
    SMTD: 'SMTD',
    GAP: 'Gap',
    QUESTION: 'Question',
    KT_SESSIONS: 'KT Sessions',
    KT_TOPICS: 'KT Topics',
    PLAYBACK_SESSION: 'Playback Sessions',
    SESSIONS: 'Sessions',
    TOPICS: 'Topics',
    INCOMPLETE: 'Incomplete',
    RECORDINGS: 'Recordings'
}

// export const KNOWLEDGE_ARTIFACT_TYPE = {
//     SOW: "SOW",
//     BUSINESS_CAPABILITY_MAP: "Business Capability Map",
//     APPLICATION_INVENTORY: "Application Inventory",
//     STAKEHOLDER_CONTACTS: "Stakeholder Contacts",
//     GOVERNANCE_PLAN: "Governance Plan",
//     OTHERS: "Others",
//     DESIGN_DOCUMENTS: "Design Documents",
//     SOPS: "Standard Operating Procedures (SOPs)"
// }

export const VIEW_ICON_PAGE = {
    KNOWLEDGE_ARTIFACT: "Knowledge Artifacts",
    TRANSITION_REVIEW: "Transition Review",
    PLAYBACK_SESSION: "Playback Sessions",
    KT_STATUS: "KT Status",
    KT_REVIEW: "KT Review"
}

export const VIEW_STATUS = "Pending"
export const OPEN_SESSION = "Open"
export const COMPLETE_SESSION = "Completed"
export const VIEW_SOURCE = "Playback"
export const SESSIONS = "Sessions"

export const LEVEL = {
    APPLICATION_NAME: 'Application Name'
}

export const I18N_SUPPORTED_LANGUAGES = [
    { name: 'EN', value: 'en', },
    { name: 'FR', value: 'fr' }
];


export const TRANSITION_MANAGER_TABS_ROUTES = [
    '/TransitionManagerDashboard/Dashboard',
    '/TransitionManagerDashboard/KTPlan',
    '/TransitionManagerDashboard/Data',
    '/TransitionManagerDashboard/TransitionJourney',
    '/TransitionManagerDashboard/Application',
    '/TransitionManagerDashboard/Integration',
    '/TransitionManagerDashboard/InfraCloud',
    '/TransitionManagerDashboard/Security',
    '/TransitionManagerDashboard/Network',
    '/TransitionManagerDashboard/BusinessFunctions',
    '/TransitionManagerDashboard/ITOps',
    '/SupportAnalystTransitionDashboard'
];

export const TRANSITION_DIRECTOR_TABS_ROUTES = [
    '/TransitionDirectorDashboard/Dashboard',
    '/TransitionDirectorDashboard/TransitionJourney',
    '',
    '/TransitionDirectorDashboard/TransitionJourney', // index 3 - used by project navigation
];

export const TRANSITION_ADMIN_TABS_ROUTES = [
    '/TransitionAdminDashboard/Dashboard',
    '/TransitionAdminDashboard/TransitionJourney'
];

export const SUPPORT_ANALYST_TABS_ROUTES = [
    '/TransitionAdminDashboard/Dashboard',
    '/SupportAnalystTransitionDashboard',
    '',
    '/SupportAnalystTransitionDashboard/TransitionJourney', // index 3 - used by project navigation
];

export const IT_MANAGER_TABS_ROUTES = [
    '/ITManagerDashboard',
    '/ITManagerDashboard/Dashboard',
    '',
    '/ITManagerDashboard/TransitionJourney', // index 3 - used by project navigation
];

export const getTabsRoutesByRole = (role: string) => {
    switch (role) {
        case ROLES.TRANSITION_DIRECTOR:
            return TRANSITION_DIRECTOR_TABS_ROUTES;
        case ROLES.TRANSITION_ADMIN:
            return TRANSITION_ADMIN_TABS_ROUTES;
        case ROLES.SUPPORT_ANALYST:
            return SUPPORT_ANALYST_TABS_ROUTES;
        case ROLES.IT_MANAGER:
            return IT_MANAGER_TABS_ROUTES;
        case ROLES.TRANSITION_MANAGER:
        default:
            return TRANSITION_MANAGER_TABS_ROUTES;
    }
};

export const IDLE_TIMEOUT_MS = 15;

// Note: Logo components should be imported where this constant is used
export const DEFAULT_CONNECTOR_CONFIGS = [
    {
        name: "SharePoint",
        type: "sharepoint",
        status: "not-configured"
    },
    {
        name: "ServiceNow",
        type: "servicenow",
        status: "not-configured"
    },
    {
        name: "Google Workspace",
        type: "google_workspace",
        status: "not-configured"
    },
];

export const TRANSITION_JOURNEY_NODE_SECTION = {
    COLLABORATION: 'COLLABORATION',
    DASHBOARD: 'DASHBOARD',
    WORK_LOG: 'WORK_LOGS'
}

export const CONNECTOR_ACCESS_TYPES = {
    READ: 'read_only',
    read_write: 'read_write'
};

export const CONNECTOR_DRAWER_STEPS = {
    form: 'form',
    directory: 'directory',
    upload: 'upload'
};

export const CONNECTOR_DRAWER_TITLES = {
    [CONNECTOR_DRAWER_STEPS.form]: 'Add connector',
    [CONNECTOR_DRAWER_STEPS.directory]: 'Select source folders',
    [CONNECTOR_DRAWER_STEPS.upload]: 'Upload Application Inventory'
};
export const ICON_NAMES = {
    BLUE_ARROW: 'BLUE_ARROW',
    RED_CHART: 'RED_CHARt',
    BLUE_FILE: 'BLUE_FILE',
    STATUS_APPROVED: 'STATUS_APPROVED',
    STATUS_IN_REVIEW: 'STATUS_IN_REVIEW',
    STATUS_FAILED: 'STATUS_FAILED'
}

export const ROLE_OPTIONS = {
    ADMIN: ['Admin', 'Transition Director'],
    TRANSITION_DIRECTOR: ['SME', 'Tower Lead'],
};

export const CONNECTORS_DEFAULT_PAGE_SIZE = 4;

export const DEFAULT_PAGE_NUMBER = 0;

export const CONNECTOR_MAPPING_STATUS = {
    MAPPED: 'mapped',
    UNMAPPED: 'unmapped',
    AVAILABLE: 'available'
};

export const CONNECTOR_STATUS = {
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    ERROR: 'error',
    AVAILABLE: 'available'
};

export const CONNECTOR_PROCESS_STATUS = {
    AVAILABLE: 'available',
    PROCESSING: 'Processing',
    COMPLETED: 'completed',
    IN_PROGRESS: 'in_progress',
    FAILED: 'failed',
    PROCESSED: 'Processed',
    MAPPED: 'mapped',
    CONNECTOR_AVAILABLE: 'Available'
};

export const LEARN_CONNECTOR_STATUS = {
    COMPLETED: 'Completed',
    IN_PROGRESS: 'In Progress',
}

export const CONNECTOR_LABELS = {
    MAPPED: 'Mapped',
    MAP_TO_PROJECT: 'Map to Project',
    UNMAP_FROM_PROJECT: 'Unmap from Project',
    PROCESS: 'Process'
};

export const CONNECTOR_STATUS_TYPES = {
    NOT_CONFIGURED: 'not-configured',
    CONFIGURED: 'configured',
    VIEW_ONLY: 'view-only'
};

export const WORKLOG_STATUS = {
    SUCCESS: 'Success',
    FAILURE: 'Failure'
}

export const BUTTONS = {
    CREATE: 'Create',
    NEXT: 'Next'
}

export const FORM_FIELD_CONSTANTS = {
    INPUT_TYPES: {
        TEXT: 'text',
        PASSWORD: 'password',
        NUMBER: 'number'
    },
    FIELD_IDENTIFIERS: {
        PASSWORD: 'password',
        DESCRIPTION: 'description'
    }
}

export const PROJECT_NAME_TOOLTIP_CHAR_LIMIT = 31;
export const PROJECT_DESCRIPTION_TOOLTIP_CHAR_LIMIT = 120;
export const PROJECT_DESCRIPTION_PROJECT_CARD_TOOLTIP_CHAR_LIMIT = 38;
export const CREATE_PROJECT_START_DATE_RANGE = Number(getEnv('VITE_CREATE_PROJECT_START_DATE_RANGE')) || 7;
export const CREATE_PROJECT_START_END_DATE_DIFF = Number(getEnv('VITE_CREATE_PROJECT_START_END_DATE_DIFF')) || 10;
export const WORK_LOG_ENABLED_TRANSITION_JOURNEY_VIEWS = [
    TRANSITION_JOURNEY_NODE_TYPE.CONNECTORS,
    TRANSITION_JOURNEY_NODE_TYPE.PROJECT_DETAILS,
    TRANSITION_JOURNEY_NODE_TYPE.DERIVE_KNOWLEDGE,
    TRANSITION_JOURNEY_NODE_TYPE.AUTOMATED_TICKET_ANALYSIS,
    TRANSITION_JOURNEY_NODE_TYPE.KNOWLEDGE_COVERAGE_ASSESSMENT,
    TRANSITION_JOURNEY_NODE_TYPE.KT_STATUS,
    TRANSITION_JOURNEY_NODE_TYPE.KT_REVIEW,
    TRANSITION_JOURNEY_NODE_TYPE.PLAYBACK_SESSIONS,
    TRANSITION_JOURNEY_NODE_TYPE.PLAYBACK_ASSESSMENTS,
    TRANSITION_JOURNEY_NODE_TYPE.REVIEW_SMTD,
    TRANSITION_JOURNEY_NODE_TYPE.TRANSITION_REVIEW
]

export const OVERLAY_DISABLED_TRANSITION_JOURNEY_VIEWS = [
    TRANSITION_JOURNEY_NODE_TYPE.PROJECT_DETAILS,
    TRANSITION_JOURNEY_NODE_TYPE.CONNECTORS,
    TRANSITION_JOURNEY_NODE_TYPE.SMART_CAUSAL_CONFIG,
    TRANSITION_JOURNEY_NODE_TYPE.TEAM_COLLABORATION,
    TRANSITION_JOURNEY_NODE_TYPE.TEAM_INVITATION,
    TRANSITION_JOURNEY_NODE_TYPE.GOVERNENCE_DASHBOARD
]

export const SINGLE_TAB_TRANSITION_JOURNEY_VIEWS = [
    TRANSITION_JOURNEY_NODE_TYPE.CONNECTORS,
    // TRANSITION_JOURNEY_NODE_TYPE.PROJECT_DETAILS,
    TRANSITION_JOURNEY_NODE_TYPE.GOVERNENCE_DASHBOARD,
    TRANSITION_JOURNEY_NODE_TYPE.TEAM_COLLABORATION,
    TRANSITION_JOURNEY_NODE_TYPE.TEAM_INVITATION,
    TRANSITION_JOURNEY_NODE_TYPE.SMART_CAUSAL_CONFIG
]

export const CONFIGURE_TYPE = {
    LEVELS: 'levels',
    FEILDS: 'feilds'
}

export const statusOptions = [
    { value: 'Open', label: 'Open' },
    // { value: 'In Progress', label: 'In Progress' },
    { value: 'Completed', label: 'Completed' },
];

// KT Session Constants
export const KT_SESSION_STATUS = {
    OPEN: 'Open',
    COMPLETED: 'Completed',
    IN_PROGRESS: 'In Progress',
    YET_TO_START: 'Yet To Start',
    VIDEO_ANALYSIS_STATUS: 'Video Analysis In Progress'
};

// KT Session and Playback Filepath
export const KT_PLAYBACK_VALUE = {
    FILE_PATH: 'file_path',
    COMPARISON_FILE_PATH: 'comparison_file_path',
    STATUS: 'Status'
};

//Video Analysis Status
export const VIDEO_ANALYSIS_STATUS = {
    COMPLETED: 'completed',
    FAILED: 'failed'
}

export const KT_STATUS_OPTIONS = [
    { value: 'Completed', label: 'Completed' },
    // { value: 'Open', label: 'Open' },
    { value: 'In Progress', label: 'In Progress' },
];

export const KT_SESSION_DEFAULTS = {
    STATUS: 'Open',
    LEAF_LEVEL_NAME: 'Others',
    UNKNOWN_USER: 'Unknown User',
    DEFAULT_SESSION_ID: 'default_session_id'
};

export const KT_SESSION_MESSAGES = {
    DESCRIPTION_REQUIRED: 'transitionJourney.ktSessionMessages.descriptionRequired',
    VIDEO_PATH_REQUIRED: 'transitionJourney.ktSessionMessages.videoPathRequired',
    STATUS_REQUIRED: 'transitionJourney.ktSessionMessages.statusRequired',
    PROJECT_ID_MISSING: 'transitionJourney.ktSessionMessages.projectIdMissing',
    UPDATE_SUCCESS: 'transitionJourney.ktSessionMessages.updateSuccess',
    ADD_SUCCESS: 'transitionJourney.ktSessionMessages.addSuccess',
    UPDATE_ERROR: 'transitionJourney.ktSessionMessages.updateError',
    ADD_ERROR: 'transitionJourney.ktSessionMessages.addError',
    VIDEO_PATH_REQUIRED_FOR_COMPLETED: 'transitionJourney.ktSessionMessages.videoPathRequiredForCompleted',
    // Placeholder texts
    DESCRIPTION_PLACEHOLDER: 'transitionJourney.ktSessionMessages.descriptionPlaceholder',
    VIDEO_PATH_PLACEHOLDER: 'transitionJourney.ktSessionMessages.videoPathPlaceholder',
    // Helper texts
    DESCRIPTION_HELPER_TEXT: 'transitionJourney.ktSessionMessages.descriptionHelperText',
    VIDEO_PATH_HELPER_TEXT: 'transitionJourney.ktSessionMessages.videoPathHelperText'
};

// Modal Mode Constants
export const MODAL_MODES = {
    ADD: 'add',
    UPDATE: 'update'
};

export const DUMMY_PROJECT_ID = 'DummyProjectId';

export const ANALYSIS_STATUS = {
    COMPLETED: 'Completed',
    IN_PROGRESS: 'In Progress',
    ERROR: 'error'
};

export const STATUS_COLORS = {
    COMPLETED: {
        background: '#48bb781f',
        color: '#48bb78ff',
        theme: 'success'
    },
    APPROVED: {
        background: '#48bb781f',
        color: '#48bb78ff',
        theme: 'success'
    },
    COMPLETE: {
        background: '#48bb781f',
        color: '#48bb78ff',
        theme: 'success'
    },
    'IN_PROGRESS': {
        background: '#ffc1071f',
        color: '#ffc107ff',
        theme: 'warning'
    },
    'IN PROGRESS': {
        background: '#ffc1071f',
        color: '#ffc107ff',
        theme: 'warning'
    },
    'VIDEO ANALYSIS IN PROGRESS': {
        background: '#ffc1071f',
        color: '#ffc107ff',
        theme: 'warning'
    },
    OPEN: {
        background: '#3f9bf626',
        color: '#3f9bf6ff',
        theme: 'info'
    },
    NEW: {
        background: '#3f9bf626',
        color: '#3f9bf6ff',
        theme: 'info'
    },
    CREATED: {
        background: '#3f9bf626',
        color: '#3f9bf6ff',
        theme: 'info'
    },
    REQUIRED: {
        background: '#d32f2f2c',
        color: '#d32f2fff',
        theme: 'error'
    },
    FAILED: {
        background: '#d32f2f2c',
        color: '#d32f2fff',
        theme: 'error'
    },
    ERROR: {
        background: '#d32f2f2c',
        color: '#d32f2fff',
        theme: 'error'
    },
    DEFAULT: {
        background: '#f5f5f5',
        color: '#666666',
        theme: 'default'
    }
};

export const CONNECTOR_TOOLTIP_MESSAGES = {
    PROCESS_DISABLED_READ_WRITE_REQUIRED: 'connectors.processDisabledReadWriteRequired'
};

// Roles allowed to reupload inventory files on processed read_write connectors
export const REUPLOAD_ALLOWED_ROLES = [
    ROLES.IT_MANAGER,
];

export const DASHBOARD_MODAL_TYPES = {
    ACTIVE_PROJECTS: 'activeProjects',
    COMPLETED: 'completed',
    PENDING_REVIEWS: 'pendingReviews',
    KT_PLANNED: 'ktPlanned',
    KT_COMPLETED: 'ktCompleted'
} as const;

export type DashboardModalType = typeof DASHBOARD_MODAL_TYPES[keyof typeof DASHBOARD_MODAL_TYPES];

// Status color mapping
export const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'completed': return '#4caf50';
        case 'in_progress': return '#ff9800';
        case 'pending': return '#f44336';
        case 'on_hold': return '#9e9e9e';
        default: return '#2196f3';
    }
}

export const fieldsToHideInReadMode = ['url', 'sharepoint_url', 'path', 'folder_path'];

export const SHOW_LEARN = {
    TIME: 'time',
    STATUS: 'status',
    FORCE_COMPLETE: 'force complete',
    FILES_FAILED: 'files failed',
    CONNECTOR_NAME: 'connector name'
}

export const CLUSTER_STATUS = {
    IMPROVED: 'Improved'
}